import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, FileDown, Plus } from "lucide-react";
import { api, errMsg, inr, fmtDate, download } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Pill, ReconBanner } from "@/components/common";
import { TxnDrawer } from "@/components/TxnDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";

function FormDialog({ title, fields, onSubmit, trigger, testid }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(Object.fromEntries(fields.map(([k, , v]) => [k, v || ""])));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          {fields.map(([k, label]) => <Input key={k} placeholder={label} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${testid}-${k}`} />)}
          <Button onClick={async () => { if (await onSubmit(f)) setOpen(false); }} data-testid={`${testid}-submit`}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Checks({ s, onOpenRow, reload }) {
  const resolve = async (index) => {
    const note = window.prompt("Note: why is this line not a transaction (e.g. header/footer text)?");
    if (!note) return;
    try { await api.post(`/statements/${s.id}/issues/resolve`, { index, note }); reload(); } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      {(s.checks || []).map((c) => (
        <div key={c.key} className="rounded-lg border p-3" data-testid={`check-${c.key}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium">{c.label}{!c.mandatory && <span className="ml-2 text-[10px] text-muted-foreground">advisory</span>}</div>
            <Pill s={c.status} testid={`check-status-${c.key}`} />
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{c.detail}</div>
          {c.rows?.length > 0 && (
            <div className="mt-2 space-y-1">
              {c.rows.slice(0, 12).map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-mono">p{r.page}{r.row_no ? `·r${r.row_no}` : r.line ? `·l${r.line}` : ""} — {r.issue}{r.text ? `: ${r.text.slice(0, 60)}` : ""}</span>
                  {r.id ? <button className="text-primary hover:underline" onClick={() => onOpenRow(r.id)}>open</button> :
                    c.key === "unreadable_rows" && <button className="text-primary hover:underline" onClick={() => resolve(s.parse_issues.indexOf(s.parse_issues.find((x) => x.page === r.page && x.line === r.line)))} data-testid="resolve-unparsed-line">mark reviewed</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function StatementView() {
  const { id } = useParams();
  const { can } = useApp();
  const [s, setS] = useState(null);
  const [rows, setRows] = useState([]);
  const [pdf, setPdf] = useState(null);
  const [open, setOpen] = useState(null);
  const [bad, setBad] = useState(new Set());
  const load = async () => {
    const [a, b] = await Promise.all([api.get(`/statements/${id}`), api.get("/transactions", { params: { statement_id: id, sort: "row_no", order: 1, limit: 2000 } })]);
    setS(a.data);
    setRows(b.data.items);
    setBad(new Set((a.data.checks || []).flatMap((c) => (c.status === "fail" ? c.rows : []).map((r) => r.id))));
  };
  useEffect(() => {
    load();
    let url;
    api.get(`/statements/${id}/file`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setPdf(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!s) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const revalidate = async () => { await api.post(`/statements/${id}/revalidate`); toast.success("Re-validated"); load(); };
  const setBal = async (f) => {
    try { await api.patch(`/statements/${id}`, f); toast.success("Balances updated & re-validated"); load(); return true; } catch (e) { toast.error(await errMsg(e)); return false; }
  };
  const addRow = async (f) => {
    try { await api.post(`/statements/${id}/transactions`, { ...f, page: Number(f.page || 1), after_row_no: Number(f.after_row_no || 0), balance: f.balance || null }); toast.success("Row added & re-validated"); load(); return true; }
    catch (e) { toast.error(await errMsg(e)); return false; }
  };
  const isPdf = s.filename.toLowerCase().endsWith(".pdf");
  return (
    <div>
      <PageHeader title={s.filename} sub={`${s.account ? `${s.account.bank_name} ${s.account.masked}` : ""} · ${fmtDate(s.period_start)} – ${fmtDate(s.period_end)} · ${s.page_count} page(s) · method: ${s.extraction_method}`}>
        <Button variant="outline" size="sm" onClick={revalidate} data-testid="revalidate-button"><RefreshCw className="mr-2 h-4 w-4" />Re-run checks</Button>
        {can("admin", "accountant") && <FormDialog title="Enter balances from the PDF" testid="balances-dialog" onSubmit={setBal}
          fields={[["opening_balance", "Opening balance", s.opening_balance], ["closing_balance", "Closing balance", s.closing_balance], ["reason", "Reason (required)"]]}
          trigger={<Button variant="outline" size="sm" data-testid="edit-balances-button">Edit balances</Button>} />}
        {can("admin", "accountant") && <FormDialog title="Add a missing transaction row" testid="add-row-dialog" onSubmit={addRow}
          fields={[["txn_date", "Date (YYYY-MM-DD)"], ["narration", "Narration exactly as printed"], ["debit", "Debit"], ["credit", "Credit"], ["balance", "Running balance"], ["ref", "Ref/UTR"], ["page", "Page"], ["after_row_no", "Insert after row no."], ["reason", "Reason (required)"]]}
          trigger={<Button variant="outline" size="sm" data-testid="add-row-button"><Plus className="mr-2 h-4 w-4" />Add missing row</Button>} />}
        <Button size="sm" onClick={() => download(`/statements/${id}/report.pdf`)} data-testid="download-recon-pdf"><FileDown className="mr-2 h-4 w-4" />Reconciliation PDF</Button>
      </PageHeader>
      <ReconBanner verified={s.status === "verified"} detail={`Opening ${inr(s.opening_balance)} + Credits ${inr(s.total_credits)} − Debits ${inr(s.total_debits)} → Closing ${inr(s.closing_balance)} · ${s.row_count} rows`} />
      <div className="mt-6 hidden h-[75vh] lg:block">
        <ResizablePanelGroup direction="horizontal" className="panel">
          <ResizablePanel defaultSize={45} minSize={25}>
            {isPdf && pdf ? <iframe title="statement" src={pdf} className="h-full w-full" data-testid="pdf-viewer" /> : <div className="p-6 text-sm text-muted-foreground">{isPdf ? "Loading document…" : "Spreadsheet source — compare rows with the original file."}</div>}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={55} minSize={30}>
            <RowsTable rows={rows} bad={bad} onOpen={setOpen} />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      <div className="mt-6 h-[60vh] lg:hidden"><RowsTable rows={rows} bad={bad} onOpen={setOpen} /></div>
      <h3 className="mb-3 mt-8 font-semibold">Validation checks</h3>
      <Checks s={s} onOpenRow={setOpen} reload={load} />
      <TxnDrawer id={open} onClose={() => setOpen(null)} onChanged={load} defaultTab="details" />
    </div>
  );
}

function RowsTable({ rows, bad, onOpen }) {
  return (
    <div className="h-full overflow-auto" data-testid="extracted-rows-table">
      <table className="w-full text-xs">
        <thead className="glass sticky top-0 z-10"><tr className="text-left text-muted-foreground">
          {["Pg", "#", "Date", "Narration", "Debit", "Credit", "Balance", "Conf", ""].map((h) => <th key={h} className="px-2 py-2 font-medium">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r.id)} className={`cursor-pointer border-t transition-colors hover:bg-accent ${bad.has(r.id) ? "bg-rose-500/10" : ""}`} data-testid="extracted-row">
              <td className="num px-2 py-1.5">{r.page}</td><td className="num px-2">{r.row_no}</td><td className="num whitespace-nowrap px-2">{fmtDate(r.txn_date)}</td>
              <td className="max-w-[260px] truncate px-2 font-mono" title={r.narration}>{r.narration}</td>
              <td className="num px-2 text-right text-rose-500">{Number(r.debit) ? inr(r.debit, false) : ""}</td>
              <td className="num px-2 text-right text-emerald-500">{Number(r.credit) ? inr(r.credit, false) : ""}</td>
              <td className="num px-2 text-right">{inr(r.balance, false)}</td>
              <td className={`num px-2 ${r.confidence < 0.8 && !r.extraction_verified ? "text-amber-500" : "text-muted-foreground"}`}>{Math.round(r.confidence * 100)}</td>
              <td className="px-2">{r.dup_status !== "new" && <Pill s={r.dup_status} />}{r.source === "manual" && <span className="text-[10px] text-primary">manual</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
